import { doodBlockConfig, propertyValue } from "./dood-block-config";
import type { SlotDefinition } from "./bloc-definition";

/**
 * Migration proof: `ShopSearchPage` re-declared through `doodBlockConfig`.
 *
 * The resulting `shopSearchPageDescriptor` is structurally compatible with
 * the legacy dashboard `IBlockDescriptor`, and `shopSearchPageBlocDefinition`
 * feeds the CB2 Studio block tree / inspector.
 *
 * Page props are independent — no `sharedFields` entries; those are reserved
 * for props whose type or visibility reacts to another declared field.
 */
const shopSearchPageSlots: readonly SlotDefinition[] = [
  {
    key: "body",
    name: "pages.blocks.shop-search-page.body.label",
    accept: "all",
    slotItemFormat: "ids",
    defaultBlocks: [
      { id: "header-block", type: "HeaderBlockComponent" },
      { id: "shop-around-search-block", type: "ShopAroundListHeaderBlockComponent" },
      { id: "shop-around-list-block", type: "ShopAroundListBodyBlockComponent" },
    ],
  },
  {
    key: "footer",
    name: "pages.blocks.shop-search-page.footer.label",
    accept: "explicitOnly",
    allowedTypes: ["BigFooterBlock", "LegalsFooterBlock", "HtmlBlock"],
    slotItemFormat: "ids",
  },
];

export const {
  shopSearchPageDescriptor,
  shopSearchPageBlocDefinition,
  composeShopSearchPageConfig,
  shopSearchPageDashboardLayout,
  shopSearchPageScenarios,
  shopSearchPageConfig,
  shopSearchPageProperties,
  shopSearchPageResolveConfig,
} = doodBlockConfig(
  {
    name: "Shop search page",
    internal_name: "Shop search page",
    url: "shops",
    defaultParent: [/^Main Layout$/i],
    componentType: "ShopSearchPageComponent",
    category: "page",
    description: "Shop search listing page with address search and shop cards.",
    keywords: ["shop", "search", "page", "listing", "restaurants"],
    isRootBlock: true,
    slots: shopSearchPageSlots,
    sharedFields: {},
  },

  () => ({
    background_image: propertyValue({
      type: "image",
      value: "",
      options: {
        label: "pages.blocks.shop-search-page.background-image.label",
        help: "pages.blocks.shop-search-page.background-image.help",
      },
    }),
    background_color: propertyValue({
      type: "color",
      value: { type: "hex", value: "#FFFFFF" },
      options: {
        label: "pages.blocks.shop-search-page.background-color.label",
        default_type: "hex",
        default_value: "#FFFFFF",
      },
    }),
    meta_description: propertyValue({
      type: "textarea",
      value: "",
      options: {
        label: "pages.blocks.shop-search-page.meta-description.label",
        help: "pages.blocks.shop-search-page.meta-description.help",
        rows: 3,
      },
    }),
  }),

  () => ({
    tabs: [
      {
        label: "pages.blocks.shop-search-page.tab.content.label",
        fieldsets: [
          {
            legend: "pages.blocks.shop-search-page.fieldset.appearance.label",
            fields: ["background_color", "background_image"],
          },
          {
            legend: "pages.blocks.shop-search-page.fieldset.seo.label",
            fields: ["meta_description"],
          },
          {
            legend: "pages.blocks.shop-search-page.fieldset.blocks.label",
            fields: ["body", "footer"],
          },
        ],
      },
    ],
  }),
);
